'use client';

import { useEffect } from 'react';
import { Calendar, Baby, Hand, Coffee, Languages, Heart } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import type {
  ChurchEvent,
  EventSession,
  ContentSlide,
  CardTheme,
  EventServiceTime,
} from '@/types/serve-finder';
import { Img, useAssetUrl } from '@/lib/asset-url';

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export function isLightColor(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 130;
}

export function shortTime(time: string): string {
  return time.replace(':00', '');
}

// ---------------------------------------------------------------------------
// Amenity maps
// ---------------------------------------------------------------------------

export const AMENITY_ICON: Record<string, React.FC<{ className?: string }>> = {
  baby: Baby,
  hand: Hand,
  coffee: Coffee,
  asl: Languages,
  heart: Heart,
};

export const AMENITY_LABEL: Record<string, string> = {
  baby: 'Kids Programming (0–4yrs)',
  heart: 'Special Needs Kids (0–14yrs)',
  hand: 'Special Needs',
  asl: 'ASL Interpretation',
  coffee: 'Café Open',
};

// ---------------------------------------------------------------------------
// ServiceTimesGrid
// ---------------------------------------------------------------------------

export function ServiceTimesGrid({
  times,
  theme,
  showLegend = false,
  showCapacity = false,
}: {
  times: EventServiceTime[];
  theme: CardTheme | null;
  showLegend?: boolean;
  showCapacity?: boolean;
}) {
  const grouped = times.reduce<Record<string, EventServiceTime[]>>((acc, t) => {
    (acc[t.Day] ??= []).push(t);
    return acc;
  }, {});

  const days = Object.keys(grouped);
  const allAmenities = [...new Set(times.flatMap((t) => t.Amenities))];

  return (
    <div className="space-y-3">
      {days.map((day) => (
        <div key={day}>
          <div
            className="mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase"
            style={{ color: theme?.Body_Color ?? undefined }}
          >
            <Calendar className={`h-3.5 w-3.5 ${theme ? '' : 'text-muted-foreground'}`} />
            <span className={theme ? '' : 'text-muted-foreground'}>{day}</span>
          </div>

          <div
            className={
              showCapacity
                ? 'grid grid-cols-2 gap-2 sm:grid-cols-3'
                : 'flex flex-wrap gap-x-4 gap-y-1.5'
            }
          >
            {grouped[day].map((t) => (
              <div
                key={`${day}-${t.Time}`}
                className={
                  showCapacity ? 'flex flex-col gap-1.5 border p-2.5' : 'flex items-center gap-1.5'
                }
                style={
                  showCapacity
                    ? {
                        borderColor: theme?.Border_Color ?? 'var(--border)',
                        backgroundColor: theme ? 'rgba(255,255,255,0.05)' : 'var(--background)',
                      }
                    : undefined
                }
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className={`font-extrabold ${showCapacity ? 'text-lg' : 'text-base'}`}
                    style={{ color: theme?.Title_Color ?? undefined }}
                  >
                    <span className={theme ? '' : 'text-foreground'}>{shortTime(t.Time)}</span>
                  </span>
                  {t.Amenities.length > 0 && (
                    <div className="flex gap-0.5" style={{ color: theme?.Body_Color ?? undefined }}>
                      {t.Amenities.map((a) => {
                        const Icon = AMENITY_ICON[a];
                        return Icon ? (
                          <Icon
                            key={a}
                            className={`h-3.5 w-3.5 ${theme ? '' : 'text-muted-foreground'}`}
                          />
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                {showCapacity && t.Capacity_Pct != null && (
                  <div className="flex items-center gap-2">
                    <div
                      className="h-1.5 flex-1 overflow-hidden"
                      style={{ backgroundColor: theme ? 'rgba(255,255,255,0.1)' : 'var(--muted)' }}
                    >
                      <div
                        className="h-full"
                        style={{
                          width: `${t.Capacity_Pct}%`,
                          backgroundColor: theme?.Accent_Color ?? 'var(--brand-secondary)',
                        }}
                      />
                    </div>
                    <span
                      className="text-[10px] font-semibold"
                      style={{ color: theme?.Body_Color ?? undefined }}
                    >
                      <span className={theme ? '' : 'text-muted-foreground'}>
                        {t.Capacity_Pct}%
                      </span>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {showLegend && allAmenities.length > 0 && (
        <div
          className="flex flex-wrap gap-x-4 gap-y-1 border-t pt-3"
          style={{ borderColor: theme?.Border_Color ?? 'var(--border)' }}
        >
          {allAmenities.map((a) => {
            const Icon = AMENITY_ICON[a];
            return Icon ? (
              <div
                key={a}
                className="flex items-center gap-1.5"
                style={{ color: theme?.Body_Color ?? undefined }}
              >
                <Icon className={`h-3.5 w-3.5 ${theme ? '' : 'text-muted-foreground'}`} />
                <span className={`text-xs ${theme ? '' : 'text-muted-foreground'}`}>
                  {AMENITY_LABEL[a] ?? a}
                </span>
              </div>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EventContentSlider
// ---------------------------------------------------------------------------

export function EventContentSlider({
  slides,
  theme,
  currentIndex,
  onIndexChange,
}: {
  slides: ContentSlide[];
  theme: CardTheme | null;
  currentIndex: number;
  onIndexChange: (i: number) => void;
}) {
  const resolve = useAssetUrl();
  useEffect(() => {
    const interval = setInterval(() => {
      onIndexChange((currentIndex + 1) % slides.length);
    }, 4500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, slides.length]);

  const slide = slides[currentIndex];
  const lightBG = !!(slide.BG_Color && isLightColor(slide.BG_Color));

  const themeHeadingClashesBG =
    theme?.Title_Color &&
    slide.BG_Color &&
    theme.Title_Color.toLowerCase().slice(0, 4) === slide.BG_Color.toLowerCase().slice(0, 4);
  const labelColor = lightBG
    ? 'rgba(0,0,0,0.5)'
    : (theme?.Subtitle_Color ?? 'rgba(255,255,255,0.6)');
  const headingColor = themeHeadingClashesBG
    ? '#2fbfaf'
    : (theme?.Title_Color ?? (lightBG ? '#1a1a1a' : '#ffffff'));
  const blurbColor = lightBG ? 'rgba(0,0,0,0.6)' : (theme?.Body_Color ?? 'rgba(255,255,255,0.8)');

  const isTagline = !slide.Label && !slide.Blurb;
  const isPerson = !!slide.Image_URL;

  const personSlideIndices = slides.map((s, i) => (s.Image_URL ? i : -1)).filter((i) => i >= 0);
  const personOrdinal = personSlideIndices.indexOf(currentIndex);
  const imageOnLeft = isPerson && personOrdinal % 2 === 1;

  return (
    <div className="relative flex w-full flex-col px-4 py-2" style={{ minHeight: 120 }}>
      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            className="absolute inset-0 flex"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            <div
              className="relative flex flex-1 overflow-hidden"
              style={{ backgroundColor: slide.BG_Color ?? undefined }}
            >
              {slide.BG_Image && (
                <Img
                  src={slide.BG_Image}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}

              {slide.BG_Color && (
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${resolve('/Hope-365/circles-stars-white.png')})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: lightBG ? 0.08 : 0.15,
                  }}
                />
              )}

              {isPerson && (
                <div
                  className={`absolute top-2 bottom-2 flex w-[42%] items-center justify-center ${
                    imageOnLeft ? 'left-2' : 'right-2'
                  }`}
                >
                  <Img src={slide.Image_URL!} alt="" className="h-full w-full object-contain" />
                </div>
              )}

              <div
                className={`relative z-10 flex flex-1 flex-col px-5 py-3 ${
                  isTagline ? 'items-center justify-center text-center' : 'justify-center'
                } ${isPerson ? (imageOnLeft ? 'items-end text-right' : '') : ''}`}
              >
                {slide.Label && (
                  <p
                    className="text-[11px] font-light tracking-[0.2em] uppercase"
                    style={{ color: labelColor }}
                  >
                    {slide.Label}
                  </p>
                )}
                <h3
                  className={`leading-[0.9] font-black whitespace-pre-line uppercase ${
                    isTagline
                      ? 'text-4xl -tracking-[0.09em]'
                      : isPerson
                        ? 'mt-1 text-3xl -tracking-[0.09em]'
                        : 'mt-1 text-2xl -tracking-[0.09em]'
                  }`}
                  style={{ color: headingColor }}
                >
                  {slide.Heading}
                </h3>
                {slide.Blurb && !isPerson && slide.Blurb.includes('|') ? (
                  <div className="mt-2 flex gap-3">
                    {slide.Blurb.split('|').map((part, i) => (
                      <div
                        key={i}
                        className={`text-xs font-semibold tracking-wide uppercase ${i > 0 ? 'border-l border-white/25 pl-3' : ''}`}
                        style={{ color: blurbColor }}
                      >
                        {part.trim()}
                      </div>
                    ))}
                  </div>
                ) : slide.Blurb ? (
                  <p
                    className={`mt-1.5 leading-[0.95] ${
                      isPerson ? 'text-base font-black -tracking-[0.09em] uppercase' : 'text-sm'
                    }`}
                    style={{
                      color: isPerson
                        ? lightBG
                          ? '#8b35c4'
                          : 'rgba(255,255,255,0.85)'
                        : blurbColor,
                    }}
                  >
                    {slide.Blurb}
                  </p>
                ) : null}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ServiceTimesSlider
// ---------------------------------------------------------------------------

interface ServiceSlide {
  label: string;
  icon: React.FC<{ className?: string }>;
  bg: string;
  bgImage?: string;
  bgImageOpacity?: number;
  headingColor: string;
  textColor: string;
  subtitleColor: string;
  align: 'left' | 'right' | 'center';
  render: () => React.ReactNode;
}

export function ServiceTimesSlider({
  event,
  session,
  theme,
  currentIndex,
  onIndexChange,
}: {
  event: ChurchEvent;
  session: EventSession;
  theme: CardTheme | null;
  currentIndex: number;
  onIndexChange: (i: number) => void;
}) {
  const resolve = useAssetUrl();
  const times = session.Service_Times ?? [];
  const allHaveKids = times.length > 0 && times.every((t) => t.Amenities.includes('baby'));

  const slides: ServiceSlide[] = [];

  const eventFolder = event.BG_URL.substring(0, event.BG_URL.lastIndexOf('/') + 1);

  const renderDayTimes = (grouped: Record<string, EventServiceTime[]>) => {
    const days = Object.entries(grouped);
    if (days.length > 1) {
      return (
        <div className="flex gap-x-6">
          {days.map(([day, dayTimes]) => (
            <div key={day}>
              <p
                className="mb-0.5 text-lg leading-none font-black uppercase"
                style={{ color: 'var(--slide-heading)' }}
              >
                {day}
              </p>
              <div className="flex flex-wrap gap-x-2">
                {dayTimes.map((t) => (
                  <span
                    key={`${day}-${t.Time}`}
                    className="text-base font-extrabold"
                    style={{ color: 'var(--slide-text)' }}
                  >
                    {shortTime(t.Time)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }
    const [day, dayTimes] = days[0];
    return (
      <div className="flex items-baseline gap-2">
        <span
          className="text-lg leading-none font-black uppercase"
          style={{ color: 'var(--slide-heading)' }}
        >
          {day}
        </span>
        <div className="flex flex-wrap gap-x-2">
          {dayTimes.map((t) => (
            <span
              key={`${day}-${t.Time}`}
              className="text-base font-extrabold"
              style={{ color: 'var(--slide-text)' }}
            >
              {shortTime(t.Time)}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const palette = [
    { bg: '#17392d', heading: '#d4a08a', text: '#ffffff', subtitle: 'rgba(255,255,255,0.5)' },
    { bg: '#d4a08a', heading: '#17392d', text: '#1e3a2c', subtitle: 'rgba(23,57,45,0.55)' },
    { bg: '#0f2a20', heading: '#e8c9a8', text: '#d4a08a', subtitle: 'rgba(212,160,138,0.55)' },
    { bg: '#2c5a46', heading: '#f5e6d3', text: '#ffffff', subtitle: 'rgba(255,255,255,0.5)' },
  ];

  slides.push({
    label: 'Service Times',
    icon: Calendar,
    align: 'left',
    bg: palette[0].bg,
    bgImage: `${eventFolder}SlideBGs/serviceTimes.jpg`,
    bgImageOpacity: 0.15,
    headingColor: palette[0].heading,
    textColor: palette[0].text,
    subtitleColor: palette[0].subtitle,
    render: () => {
      const grouped = times.reduce<Record<string, EventServiceTime[]>>((acc, t) => {
        (acc[t.Day] ??= []).push(t);
        return acc;
      }, {});
      return renderDayTimes(grouped);
    },
  });

  const kidsServices = times.filter((t) => t.Amenities.includes('baby'));
  if (kidsServices.length > 0) {
    slides.push({
      label: 'Kids Programming',
      icon: Baby,
      align: 'center',
      bg: palette[1].bg,
      bgImage: `${eventFolder}SlideBGs/Kids.jpg`,
      bgImageOpacity: 0.15,
      headingColor: palette[1].heading,
      textColor: palette[1].text,
      subtitleColor: palette[1].subtitle,
      render: () => (
        <div className="space-y-1">
          <p
            className="text-2xl leading-none font-black uppercase"
            style={{ color: 'var(--slide-text)' }}
          >
            {allHaveKids ? 'All Services' : `${kidsServices.length} of ${times.length} services`}
          </p>
          <p className="text-[10px] leading-snug" style={{ color: 'var(--slide-subtitle)' }}>
            Woodside Kids environments open for ages 0–4yrs
          </p>
        </div>
      ),
    });
  }

  const specialNeedsServices = times.filter((t) => t.Amenities.includes('heart'));
  if (specialNeedsServices.length > 0) {
    slides.push({
      label: 'Special Needs',
      icon: Heart,
      align: 'right',
      bg: palette[2].bg,
      bgImage: `${eventFolder}SlideBGs/specialNeeds.jpg`,
      bgImageOpacity: 0.15,
      headingColor: palette[2].heading,
      textColor: palette[2].text,
      subtitleColor: palette[2].subtitle,
      render: () => {
        const grouped = specialNeedsServices.reduce<Record<string, EventServiceTime[]>>(
          (acc, t) => {
            (acc[t.Day] ??= []).push(t);
            return acc;
          },
          {}
        );
        return renderDayTimes(grouped);
      },
    });
  }

  const aslServices = times.filter((t) => t.Amenities.includes('asl'));
  if (aslServices.length > 0) {
    slides.push({
      label: 'ASL Interpretation',
      icon: Languages,
      align: 'left',
      bg: palette[3 % palette.length].bg,
      bgImage: `${eventFolder}SlideBGs/ASL.png`,
      bgImageOpacity: 0.15,
      headingColor: palette[3 % palette.length].heading,
      textColor: palette[3 % palette.length].text,
      subtitleColor: palette[3 % palette.length].subtitle,
      render: () => {
        const grouped = aslServices.reduce<Record<string, EventServiceTime[]>>((acc, t) => {
          (acc[t.Day] ??= []).push(t);
          return acc;
        }, {});
        return renderDayTimes(grouped);
      },
    });
  }

  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      onIndexChange((currentIndex + 1) % slides.length);
    }, 4500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, slides.length]);

  const activeIndex = currentIndex % slides.length;
  const activeSlide = slides[activeIndex];
  return (
    <div className="flex h-full items-stretch px-4 py-2">
      <div
        className="relative w-full overflow-hidden px-3 py-2 transition-colors duration-500"
        style={{ backgroundColor: activeSlide.bg }}
      >
        {activeSlide.bgImage && (
          <Img
            src={activeSlide.bgImage}
            alt=""
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
            style={{ opacity: activeSlide.bgImageOpacity ?? 1 }}
          />
        )}

        <div className="relative z-10 grid h-full" style={{ gridTemplate: '1fr / 1fr' }}>
          {slides.map((s, i) => {
            const isActive = i === activeIndex;
            const textAlign =
              s.align === 'center'
                ? 'text-center items-center'
                : s.align === 'right'
                  ? 'text-right items-end'
                  : 'text-left items-start';
            return (
              <div
                key={i}
                className={`flex flex-col justify-center ${textAlign}`}
                style={
                  {
                    gridArea: '1 / 1',
                    opacity: isActive ? 1 : 0,
                    transition: 'opacity 0.4s ease-in-out',
                    pointerEvents: isActive ? 'auto' : 'none',
                    '--slide-heading': s.headingColor,
                    '--slide-text': s.textColor,
                    '--slide-subtitle': s.subtitleColor,
                  } as React.CSSProperties
                }
                aria-hidden={!isActive}
              >
                <p
                  className="mb-0.5 text-[10px] font-semibold tracking-[0.2em] uppercase"
                  style={{ color: s.headingColor }}
                >
                  {s.label}
                </p>
                {s.render()}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Returns the count of service-time slides for dot indicators */
export function getServiceSlideCount(
  firstSessionWithTimes: EventSession | null | undefined
): number {
  if (!firstSessionWithTimes?.Service_Times) return 0;
  const t = firstSessionWithTimes.Service_Times;
  let count = 1;
  if (t.some((s) => s.Amenities.includes('baby'))) count++;
  if (t.some((s) => s.Amenities.includes('heart'))) count++;
  if (t.some((s) => s.Amenities.includes('asl'))) count++;
  return count;
}
